import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AuditNote {
  id: string;
  auditId: string;
  note: string;
  createdAt: string;
  updatedAt: string;
}

export interface AuditStatus {
  auditId: string;
  status: 'new' | 'contacted' | 'qualified' | 'won' | 'lost';
  priority: 'hot' | 'warm' | 'cold';
  notes: AuditNote[];
  lastContactDate?: string;
  nextFollowUpDate?: string;
}

interface AdminDataState {
  auditStatuses: Record<string, AuditStatus>;
  setAuditStatus: (auditId: string, status: AuditStatus['status']) => void;
  setPriority: (auditId: string, priority: AuditStatus['priority']) => void;
  addNote: (auditId: string, note: string) => void;
  updateNote: (auditId: string, noteId: string, note: string) => void;
  deleteNote: (auditId: string, noteId: string) => void;
  setLastContactDate: (auditId: string, date: string) => void;
  setNextFollowUpDate: (auditId: string, date: string) => void;
  getAuditStatus: (auditId: string) => AuditStatus;
}

export const useAdminDataStore = create<AdminDataState>()(
  persist(
    (set, get) => ({
      auditStatuses: {},
      
      setAuditStatus: (auditId, status) => {
        set((state) => ({
          auditStatuses: {
            ...state.auditStatuses,
            [auditId]: {
              ...state.auditStatuses[auditId],
              auditId,
              status,
              notes: state.auditStatuses[auditId]?.notes || [],
              priority: state.auditStatuses[auditId]?.priority || 'warm',
            },
          },
        }));
      },
      
      setPriority: (auditId, priority) => {
        set((state) => ({
          auditStatuses: {
            ...state.auditStatuses,
            [auditId]: {
              ...state.auditStatuses[auditId],
              auditId,
              priority,
              status: state.auditStatuses[auditId]?.status || 'new',
              notes: state.auditStatuses[auditId]?.notes || [],
            },
          },
        }));
      },
      
      addNote: (auditId, note) => {
        const newNote: AuditNote = {
          id: `note-${Date.now()}`,
          auditId,
          note,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        
        set((state) => ({
          auditStatuses: {
            ...state.auditStatuses,
            [auditId]: {
              ...state.auditStatuses[auditId],
              auditId,
              status: state.auditStatuses[auditId]?.status || 'new',
              priority: state.auditStatuses[auditId]?.priority || 'warm',
              notes: [...(state.auditStatuses[auditId]?.notes || []), newNote],
            },
          },
        }));
      },
      
      updateNote: (auditId, noteId, note) => {
        set((state) => ({
          auditStatuses: {
            ...state.auditStatuses,
            [auditId]: {
              ...state.auditStatuses[auditId],
              notes: state.auditStatuses[auditId]?.notes.map((n) =>
                n.id === noteId
                  ? { ...n, note, updatedAt: new Date().toISOString() }
                  : n
              ) || [],
            },
          },
        }));
      },
      
      deleteNote: (auditId, noteId) => {
        set((state) => ({
          auditStatuses: {
            ...state.auditStatuses,
            [auditId]: {
              ...state.auditStatuses[auditId],
              notes: state.auditStatuses[auditId]?.notes.filter((n) => n.id !== noteId) || [],
            },
          },
        }));
      },
      
      setLastContactDate: (auditId, date) => {
        set((state) => ({
          auditStatuses: {
            ...state.auditStatuses,
            [auditId]: {
              ...state.auditStatuses[auditId],
              auditId,
              status: state.auditStatuses[auditId]?.status || 'new',
              priority: state.auditStatuses[auditId]?.priority || 'warm',
              notes: state.auditStatuses[auditId]?.notes || [],
              lastContactDate: date,
            },
          },
        }));
      },
      
      setNextFollowUpDate: (auditId, date) => {
        set((state) => ({
          auditStatuses: {
            ...state.auditStatuses,
            [auditId]: {
              ...state.auditStatuses[auditId],
              auditId,
              status: state.auditStatuses[auditId]?.status || 'new',
              priority: state.auditStatuses[auditId]?.priority || 'warm',
              notes: state.auditStatuses[auditId]?.notes || [],
              nextFollowUpDate: date,
            },
          },
        }));
      },
      
      getAuditStatus: (auditId) => {
        return get().auditStatuses[auditId] || {
          auditId,
          status: 'new',
          priority: 'warm',
          notes: [],
        };
      },
    }),
    {
      name: 'admin-data-storage',
    }
  )
);
